import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { UpdateFilterFunction } from "hooks/useFilter";
import {
  FilingDeadlineFilter,
  FilingDeadlineFilterType,
  StateRegistrationStatusFilter,
  StateRegistrationStatusFilterType,
} from "store/apis/StateRegistrations";

export type StateComplianceFilterType = {
  STATUS: StateRegistrationStatusFilterType[];
  DEADLINE: FilingDeadlineFilterType[];
};

const STATUS_OPTIONS = [
  {
    value: StateRegistrationStatusFilter.NOT_REGISTERED,
    label: "Not registered",
  },
  {
    value: StateRegistrationStatusFilter.FILING_IN_PROGRESS,
    label: "Filing in progress",
  },
  { value: StateRegistrationStatusFilter.MISSING_INFO, label: "Missing info" },
  { value: StateRegistrationStatusFilter.REGISTERED, label: "Registered" },
  { value: StateRegistrationStatusFilter.INCORPORATED, label: "Incorporated" },
];

const STATUS_OPTION_LABELS: Record<StateRegistrationStatusFilterType, string> =
  STATUS_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.value]: option.label }),
    {} as Record<StateRegistrationStatusFilterType, string>
  );

const DEADLINE_OPTIONS = [
  { value: FilingDeadlineFilter.OVERDUE, label: "Overdue" },
  { value: FilingDeadlineFilter.DUE, label: "Due soon" },
];

const DEADLINE_OPTION_LABELS: Record<FilingDeadlineFilterType, string> =
  DEADLINE_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.value]: option.label }),
    {} as Record<FilingDeadlineFilterType, string>
  );

const StatusFilter = ({
  filter,
  updateFilter,
}: {
  filter: StateComplianceFilterType;
  updateFilter: UpdateFilterFunction<StateComplianceFilterType>;
}) => {
  const selectedStatuses = filter.STATUS || [];

  return (
    <MultiSelectFilter
      options={STATUS_OPTIONS}
      selected={selectedStatuses}
      onChange={(newValues) =>
        updateFilter("STATUS", newValues as StateRegistrationStatusFilterType[])
      }
      isSearchable={false}
      isSelectAll={false}
    />
  );
};

const DeadlineFilter = ({
  filter,
  updateFilter,
}: {
  filter: StateComplianceFilterType;
  updateFilter: UpdateFilterFunction<StateComplianceFilterType>;
}) => {
  const selectedDeadlines = filter.DEADLINE || [];

  return (
    <MultiSelectFilter
      options={DEADLINE_OPTIONS}
      selected={selectedDeadlines}
      onChange={(newValues) =>
        updateFilter("DEADLINE", newValues as FilingDeadlineFilterType[])
      }
      isSearchable={false}
      isSelectAll={false}
    />
  );
};

type StateComplianceFilterProps = {
  filter: StateComplianceFilterType;
  updateFilter: UpdateFilterFunction<StateComplianceFilterType>;
};

export const StateComplianceFilter = ({
  filter,
  updateFilter,
}: StateComplianceFilterProps) => {
  const selectedStatuses = filter.STATUS || [];
  const selectedDeadlines = filter.DEADLINE || [];
  const selectedCount = selectedStatuses.length + selectedDeadlines.length;

  const removeStatus = (status: StateRegistrationStatusFilterType) => {
    updateFilter(
      "STATUS",
      selectedStatuses.filter((s) => s !== status)
    );
  };

  const removeDeadline = (deadline: FilingDeadlineFilterType) => {
    updateFilter(
      "DEADLINE",
      selectedDeadlines.filter((d) => d !== deadline)
    );
  };

  return (
    <Filter.Root
      defaultValue="status"
      title={
        selectedCount > 0 ? (
          <span className="t-text-text-30">({selectedCount})</span>
        ) : null
      }
      capsule={
        <>
          {selectedStatuses.map((status) => (
            <Chip
              key={status}
              onRemove={() => removeStatus(status)}
              isActive
              isRemovable
              isFixedFilter
              filterType="status"
            >
              {STATUS_OPTION_LABELS[status]}
            </Chip>
          ))}
          {selectedDeadlines.map((deadline) => (
            <Chip
              key={deadline}
              onRemove={() => removeDeadline(deadline)}
              isActive
              isRemovable
              isFixedFilter
              filterType="deadline"
            >
              {DEADLINE_OPTION_LABELS[deadline]}
            </Chip>
          ))}
        </>
      }
    >
      <Filter.Portal size="small">
        <Filter.List size="small">
          <Filter.ListItem value="status">Status</Filter.ListItem>
          <Filter.ListItem value="deadline">Filing deadline</Filter.ListItem>
        </Filter.List>
        <Filter.Body value="status">
          <StatusFilter filter={filter} updateFilter={updateFilter} />
        </Filter.Body>
        <Filter.Body value="deadline">
          <DeadlineFilter filter={filter} updateFilter={updateFilter} />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
